import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

interface DeckStageProps {
  page: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Title/meta row above the deck (name, rank, filter…). */
  header: ReactNode;
  /** The 4×2 deck grid — flanked by the big pager arrows, at the deck's height. */
  grid: ReactNode;
  /** Stats block that fills the space under the grid. */
  stats: ReactNode;
  /** Action row pinned at the bottom. */
  actions: ReactNode;
  /** Overlay (e.g. the coach) painted over the whole stage when open. */
  overlay?: ReactNode;
}

/**
 * One deck, filling the panel — no scroll, no reflow between pages.
 *
 * The layout is a fixed column: a header, then the deck grid **flanked by two
 * big arrows sized to the grid's own height** (so the pager control sits right
 * beside the deck it moves, not as a thin strip on top), then the stats filling
 * the remaining space, then the actions pinned to the bottom. Paging swaps only
 * the contents of these slots — the frame never moves.
 *
 * Arrows only: the horizontal axis belongs to the Hub's section switcher, so a
 * left/right swipe here would change section instead of deck.
 */
export function DeckStage({
  page,
  total,
  onPrev,
  onNext,
  header,
  grid,
  stats,
  actions,
  overlay,
}: DeckStageProps) {
  return (
    <div className="relative flex h-full flex-col gap-2">
      <div className="shrink-0">{header}</div>

      {/* Grid + arrows. `items-stretch` makes each arrow as tall as the grid. */}
      <div className="flex shrink-0 items-stretch gap-1.5">
        <PagerArrow dir="prev" disabled={page <= 0} onClick={onPrev} />
        {/* px absorbs the grid's avg-elixir badge overhang without a scroller. */}
        <div className="min-w-0 flex-1 px-1.5">{grid}</div>
        <PagerArrow dir="next" disabled={page >= total - 1} onClick={onNext} />
      </div>

      {/*
        Le statistiche ora SCORRONO invece di essere tagliate.
        Prima erano `overflow-hidden`: su schermi corti le ultime barre della
        valutazione finivano sotto il bordo e non c'era modo di vederle — il
        contenuto esisteva ma era irraggiungibile, che è peggio del non averlo.
        `overscroll-contain` impedisce che l'inerzia a fine elenco trascini la
        Hub, e `pan-y` lascia l'asse orizzontale al cambio sezione.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [touch-action:pan-y]">
        {stats}
      </div>

      <div className="shrink-0">{actions}</div>

      {overlay}
    </div>
  );
}

/** Centred deck name + position (page/total), with an optional control right. */
export function StageHeader({
  name,
  page,
  total,
  children,
}: {
  name: string;
  page: number;
  total: number;
  children?: ReactNode;
}) {
  return (
    <div className="relative flex items-center justify-center gap-1.5">
      <span className="min-w-0 truncate text-sm font-semibold text-fg">{name}</span>
      <span className="shrink-0 text-[10px] tabular-nums text-fg-muted">
        {page + 1}/{total}
      </span>
      {children && <div className="absolute right-0">{children}</div>}
    </div>
  );
}

/**
 * The bottom action row: equal-width pills, with an optional error under it.
 * Uses `grid-flow-col auto-cols-fr` so it self-sizes to however many pills
 * actually render — a hidden pill (e.g. the AI coach when its flag is off)
 * leaves no empty column.
 */
export function ActionRow({ children, error }: { children: ReactNode; error?: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="grid auto-cols-fr grid-flow-col gap-1.5">{children}</div>
      {error && <p className="text-[11px] text-rose-300">{error}</p>}
    </div>
  );
}

function PagerArrow({
  dir,
  disabled,
  onClick,
}: {
  dir: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const haptics = useHaptics();
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={dir}
      disabled={disabled}
      onClick={() => {
        haptics.selection();
        onClick();
      }}
      className={cn(
        // Pastiglia PIENA, non contornata: era un bordo tenue che si leggeva
        // come decorazione del mazzo, non come comando.
        //
        // Superficie TEAL (`bg-bg-tertiary`), non accento: in accento pieno due
        // frecce ai lati del mazzo gridano più del mazzo stesso, e l'accento
        // qui non significa nulla — non è la scelta principale della schermata,
        // è il modo per sfogliare. La freccia bianca basta a renderle leggibili.
        'flex w-10 shrink-0 items-center justify-center rounded-xl bg-bg-tertiary text-white',
        'card-edge transition-opacity active:opacity-70',
        // A fine corsa resta VISIBILE ma spenta: sparendo del tutto lascerebbe
        // un buco e non si capirebbe di essere all'ultimo mazzo.
        'disabled:opacity-40',
      )}
    >
      <Icon className="h-6 w-6" strokeWidth={2.5} />
    </button>
  );
}
